import {useLayoutEffect, useRef, useState} from 'react';
import axios from 'axios';
import {toast} from 'sonner';

// Keep same-org requests ordered even when the Banking tab unmounts/remounts.
// This is a local UI lane, not a cross-tab/server concurrency guarantee.
const lanes = new Map();

export default function useBankingFeePolicy({API, token, user, setOrganization}) {
  const session = useRef(null);
  const [state, setState] = useState({pending: null, failed: false});
  const orgId = user?.org_id;
  useLayoutEffect(() => {
    const current = {active: true, running: false, desired: null};
    session.current = current;
    setState({pending: null, failed: false});
    return () => { current.active = false; current.desired = null; };
  }, [API, token, orgId]);

  const change = async desired => {
    const current = session.current;
    current.desired = desired;
    setState({pending: desired, failed: false});
    if (current.running) return;
    current.running = true;
    const key = `${API}/organizations/${orgId}`;
    const previous = lanes.get(key);
    let release;
    const lane = new Promise(resolve => { release = resolve; });
    lanes.set(key, lane);
    try {
      if (previous) await previous;
      while (current.active && current.desired !== null) {
        const passToUser = current.desired;
        const response = await axios.put(key, {pass_processing_fee: passToUser}, {headers: {Authorization: `Bearer ${token}`}});
        if (response.data?.pass_processing_fee !== passToUser) throw new Error("Fee setting was not confirmed");
        if (!current.active) return;
        setOrganization(prev => ({...prev, pass_processing_fee: passToUser}));
        if (current.desired === passToUser) {
          current.desired = null;
          setState({pending: null, failed: false});
          toast.success(passToUser ? 'All fees will be passed to users' : 'Organization will absorb all fees', {style: {color: '#166534', background: '#ecfdf3', borderColor: '#166534'}});
        }
      }
    } catch (error) {
      // A failed response may be ambiguous. Stop, never auto-retry queued writes.
      if (current.active) {
        setState({pending: null, failed: true});
        toast.error('Failed to update setting', {style: {color: '#991b1b', background: '#fff0f0', borderColor: '#991b1b'}});
      }
    } finally {
      current.running = false;
      release();
      if (lanes.get(key) === lane) lanes.delete(key);
    }
  };
  return {...state, change, retry: () => change(session.current.desired)};
}
